import React, { useEffect, useState } from 'react';
import { useNavigate } from '../lib/routerCompat';
import { useToast } from '../ToastContext';
import { db, ApplicationExistsError } from '../services/db';
import { BUDGET_FILTERS, matchesBudget } from '../lib/campaignBudget';
import { isCreatorProfileComplete } from '../lib/creatorProfile';
import { MAX_PITCH_CHARS, countWords, validatePitch } from '../lib/pitch';
import { UNSPECIFIED_BRIEF_FIELD } from '../types';
import type { ApplicationStatus, Campaign, CampaignRow, CreatorRow } from '../types';

/**
 * The creator-facing campaign feed (PIT-76): open work, from any brand.
 *
 * The mirror image of Discover — brands browse creators there, creators browse
 * campaigns here — so it borrows that view's filter vocabulary rather than
 * inventing a second way to talk about money.
 *
 * Applying happens here too (PIT-78): a card opens a pitch composer seeded from
 * the creator's profile pitch, and what it sends is a copy — editing the profile
 * later must not rewrite a pitch a brand has already read.
 */

/** A campaign as the feed consumes it: the row plus its signed brief link. */
type FeedCampaign = Campaign & Partial<Pick<CampaignRow, 'brief_path' | 'brief_name'>>;

/** How each application status reads on a card the creator already applied to. */
const STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Applied — waiting on the brand',
  accepted: 'Accepted',
  declined: 'Not selected',
  withdrawn: 'Withdrawn',
};

/** Reuses the escrow badge palette so status colour means the same thing app-wide. */
const STATUS_BADGE: Record<ApplicationStatus, string> = {
  submitted: 'badge-pending',
  accepted: 'badge-released',
  declined: 'badge-pending',
  withdrawn: 'badge-pending',
};

export default function OpportunitiesView(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const [campaigns, setCampaigns] = useState<FeedCampaign[]>([]);
  const [briefUrls, setBriefUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [query, setQuery] = useState<string>('');
  const [activeBudget, setActiveBudget] = useState<number>(0);

  // What this creator has already sent, keyed by campaign, so the board shows
  // an outcome rather than offering Apply again.
  const [applied, setApplied] = useState<Record<number, ApplicationStatus>>({});
  const [creator, setCreator] = useState<CreatorRow | null>(null);
  // The campaign whose pitch composer is open, and the draft in it.
  const [composingFor, setComposingFor] = useState<number | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [pitchTouched, setPitchTouched] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    db.getOpenCampaigns()
      .then((data) => {
        if (cancelled) return;
        const rows = data as FeedCampaign[];
        setCampaigns(rows);
        setLoading(false);
        // One signing call for the whole screen rather than one per card.
        db.getBriefUrls(rows.map((c) => c.brief_path))
          .then((urls) => {
            if (!cancelled) setBriefUrls(urls);
          })
          .catch(() => {
            // A card simply renders without its download link.
          });
      })
      .catch(() => {
        // Treat a failed load as an empty board rather than a broken page.
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Who is applying, and what they have already sent. Failures here leave the
  // board browsable — the write path re-checks everything that matters.
  useEffect(() => {
    let cancelled = false;
    Promise.all([db.getMyCreator(), db.getMyApplications()])
      .then(([row, applications]) => {
        if (cancelled) return;
        setCreator(row);
        setApplied(Object.fromEntries(applications.map((a) => [a.campaign_id, a.status])));
      })
      .catch(() => {
        // Browse-only until the next load succeeds.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const profileReady = isCreatorProfileComplete(creator);

  // Open the composer, seeded from the profile pitch. A copy: what gets sent is
  // whatever sits in the box at submit time, and editing the profile later must
  // not rewrite a pitch a brand has already read (PIT-77).
  const startApplying = (campaignId: number): void => {
    if (!profileReady) {
      showToast('Finish your creator profile before applying');
      navigate({ to: '/onboarding' });
      return;
    }
    setComposingFor(campaignId);
    setDraft(creator?.pitch ?? '');
    setPitchTouched(false);
  };

  const pitchError = validatePitch(draft, 'Write a pitch aimed at this campaign.');

  const submitApplication = async (campaignId: number): Promise<void> => {
    if (sending) return;
    if (pitchError) {
      setPitchTouched(true);
      return;
    }
    setSending(true);
    try {
      await db.applyToCampaign({ campaign_id: campaignId, pitch_text: draft.trim() });
      setApplied((prev) => ({ ...prev, [campaignId]: 'submitted' }));
      setComposingFor(null);
      showToast(db.demoMode ? 'Demo mode — application not sent' : 'Application sent');
    } catch (err) {
      if (err instanceof ApplicationExistsError) {
        // The unique index caught a second tab, or a stale board. Either way
        // the creator has applied — reflect that rather than inviting a retry.
        setApplied((prev) => ({ ...prev, [campaignId]: 'submitted' }));
        setComposingFor(null);
        showToast(err.message);
      } else {
        showToast('Could not send your application — please try again');
      }
    } finally {
      setSending(false);
    }
  };

  const budget = BUDGET_FILTERS[activeBudget];
  const q = query.trim().toLowerCase();
  const filtered = campaigns.filter((c) => {
    const matchesQuery =
      !q || [c.title, c.product].some((field) => (field ?? '').toLowerCase().includes(q));
    return matchesQuery && matchesBudget(c.budget, budget);
  });

  return (
    <div className="view">
      <div className="section-header">Open Campaigns</div>
      <div className="section-sub" style={{ marginBottom: 20 }}>
        Live briefs from brands looking for creators. Everything here is open to applications.
      </div>

      <input
        className="form-input"
        style={{ marginBottom: 16 }}
        placeholder="Search campaigns by name or product…"
        aria-label="Search campaigns"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="option-grid" style={{ marginBottom: 24 }}>
        {BUDGET_FILTERS.map((f, i) => (
          <button
            key={f.label}
            type="button"
            className={`option-btn${i === activeBudget ? ' sel' : ''}`}
            aria-pressed={i === activeBudget}
            onClick={() => setActiveBudget(i)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="panel">
          <div className="creator-name" style={{ fontSize: 22.5 }}>
            Loading campaigns…
          </div>
        </div>
      ) : filtered.length === 0 ? (
        /* Pre-launch this is the expected state, and it says so plainly rather
           than padding the board with examples that were never real. */
        <div className="panel">
          <div className="creator-name" style={{ fontSize: 22.5, marginBottom: 8 }}>
            {campaigns.length === 0 ? 'No open campaigns yet' : 'Nothing matches that'}
          </div>
          <div className="section-sub">
            {campaigns.length === 0
              ? 'Brands are still arriving. When one posts a brief, it appears here.'
              : 'Try a wider budget range, or clear the search.'}
          </div>
        </div>
      ) : (
        <div className="payment-grid">
          {filtered.map((c) => {
            const briefUrl = c.brief_path ? briefUrls[c.brief_path] : undefined;
            // Feed rows always come from the database, so the id is numeric;
            // the union on Campaign.id covers unsaved local rows elsewhere.
            const campaignId = Number(c.id);
            const status = applied[campaignId];
            return (
              <div className="payment-card" key={c.id}>
                <div className="payment-card-title">{c.title}</div>
                {c.product && <div className="payment-card-sub">{c.product}</div>}

                <div className="payment-amount" style={{ margin: '14px 0' }}>
                  {c.budget}
                </div>

                {/* What the brand actually wants made. Sits directly under the
                    budget because the two only mean something together — the
                    same money buys one TikTok video or a whole UGC ad set, and
                    a creator cannot judge the offer without both (PIT-83). */}
                <div className="my-pitch-count" style={{ marginBottom: 6 }}>
                  Deliverable: <strong>{c.deliverable || UNSPECIFIED_BRIEF_FIELD}</strong>
                </div>

                <div className="my-pitch-count" style={{ marginBottom: 12 }}>
                  Timeline: {c.deadline || 'Flexible'}
                  {c.content_style ? ` · ${c.content_style}` : ''}
                </div>

                {briefUrl && (
                  <a
                    className="btn-outline"
                    href={briefUrl}
                    download={c.brief_name ?? 'brief.pdf'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginRight: 8 }}
                  >
                    Download brief
                  </a>
                )}

                {/* Applied already — show the outcome, not another Apply. */}
                {status ? (
                  <div className={`escrow-badge ${STATUS_BADGE[status]}`} role="status">
                    {STATUS_LABEL[status]}
                  </div>
                ) : composingFor === campaignId ? (
                  <div style={{ marginTop: 14 }}>
                    <label className="form-label" htmlFor={`pitch-${campaignId}`}>
                      Your pitch for this campaign
                    </label>
                    <textarea
                      id={`pitch-${campaignId}`}
                      className={`form-input${pitchTouched && pitchError ? ' input-error' : ''}`}
                      style={{ resize: 'vertical', minHeight: 140 }}
                      placeholder="Why you, for this brief?"
                      maxLength={MAX_PITCH_CHARS}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <div className="my-pitch-meta">
                      <span className="my-pitch-count">{countWords(draft)} words</span>
                      <span className="my-pitch-count">
                        {draft.length} / {MAX_PITCH_CHARS}
                      </span>
                    </div>
                    {pitchTouched && pitchError && (
                      <div className="my-pitch-error">{pitchError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn-submit-gold"
                        style={{ width: 'auto', padding: '10px 22px' }}
                        onClick={() => submitApplication(campaignId)}
                        disabled={sending}
                      >
                        {sending ? 'Sending…' : 'Send application'}
                      </button>
                      <button
                        type="button"
                        className="btn-outline"
                        onClick={() => setComposingFor(null)}
                        disabled={sending}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => startApplying(campaignId)}
                  >
                    Apply with a pitch
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
